import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listClasses,
  listSubjects,
  listSubjectCombinations,
  getWeekConfig,
  setWeekConfig,
  getWeekSubjectPlan,
  setWeekSubjectPlan,
  getVanhoaDistribution,
  getPeriodRules,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Chuẩn hoá tên môn để so khớp: bỏ dấu, bỏ ký tự đặc biệt, thường hoá.
function norm(name) {
  return (name || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}
// Viết tắt hay dùng ở ô "Môn thi tốt nghiệp" -> tên môn chuẩn hoá.
// Lưu ý: "CN-NN"/"CN-CN" = môn Công nghệ, KHÔNG phải Ngoại ngữ.
const ALIAS = {
  van: "nguvan", su: "lichsu", dia: "dialy", ly: "vatly", hoa: "hoahoc", sinh: "sinhhoc",
  anh: "tienganh", tienganh: "tienganh",
  cn: "congnghe", cnnn: "congnghe", cncn: "congnghe", congnghe: "congnghe",
  toan: "toan", gdkt: "gdktpl", gdcd: "gdktpl", gdktpl: "gdktpl",
};
// Môn "chính" (Văn, Toán) dùng tỉ lệ cao hơn.
const isMainNorm = (n) => n === "nguvan" || n === "toan";

const HappySchoolSubjectConfig = ({ weekId, khois }) => {
  const schoolId = getActiveSchoolId();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [combos, setCombos] = useState([]);
  const [dist, setDist] = useState({ rows: [] });
  const [rules, setRules] = useState({});
  const [mode, setMode] = useState("all"); // "all" | "exam"
  const [wMain, setWMain] = useState(3); // tỉ lệ Văn/Toán
  const [wOther, setWOther] = useState(2); // tỉ lệ môn khác
  const [perBuoi, setPerBuoi] = useState(5); // số tiết văn hoá mỗi buổi
  const [edited, setEdited] = useState({}); // "classId|subjectId" -> số tiết sửa tay
  const [appliedAt, setAppliedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const reload = () => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([
      listClasses(schoolId),
      listSubjects(schoolId),
      listSubjectCombinations(schoolId),
      weekId ? getWeekConfig(schoolId, weekId) : Promise.resolve({}),
      weekId ? getVanhoaDistribution(schoolId, weekId) : Promise.resolve({ rows: [] }),
      weekId ? getWeekSubjectPlan(schoolId, weekId) : Promise.resolve({ plan: {} }),
      weekId ? getPeriodRules(schoolId, weekId) : Promise.resolve({}),
    ])
      .then(([c, s, co, wc, d, wp, pr]) => {
        setClasses(c);
        setSubjects(s);
        setCombos(co);
        setDist(d || { rows: [] });
        setRules(pr || {});
        if (wc && wc.subjectMode) setMode(wc.subjectMode);
        if (wc && wc.weightMain != null) setWMain(wc.weightMain);
        if (wc && wc.weightOther != null) setWOther(wc.weightOther);
        if (wc && wc.periodsPerVanhoaBuoi != null) setPerBuoi(wc.periodsPerVanhoaBuoi);
        setAppliedAt(wc && wc.subjectModeAppliedAt ? wc.subjectModeAppliedAt : null);
        // Seed số tiết đã áp dụng (từ plan tuần) vào ô sửa tay để hiển thị lại đúng.
        const plan = (wp && wp.plan) || {};
        const seed = {};
        Object.keys(plan).forEach((cid) => {
          Object.keys(plan[cid] || {}).forEach((sid) => { seed[`${cid}|${sid}`] = plan[cid][sid]; });
        });
        if (Object.keys(seed).length) setEdited(seed);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [schoolId, weekId]);

  if (!weekId) return <p style={{ color: "#6b7280" }}>Chưa chọn tuần TKB.</p>;
  if (loading) return <p>Đang tải...</p>;

  const subjectName = (id) => (subjects.find((s) => s.id === id) || {}).name || id;
  const comboById = Object.fromEntries(combos.map((co) => [co.id, co]));
  const subjectByNorm = Object.fromEntries(subjects.map((s) => [norm(s.name), s]));
  const distByName = Object.fromEntries((dist.rows || []).map((r) => [r.className, r]));

  // Dò ô "Môn thi tốt nghiệp" (chữ tự do) -> danh sách môn.
  const matchExam = (combo) => {
    const ids = [];
    const matched = [];
    const unmatched = [];
    String(combo.examSubjects || "").split(",").forEach((raw) => {
      const t = norm(raw);
      if (!t) return;
      let s = subjectByNorm[t] || (ALIAS[t] && subjectByNorm[ALIAS[t]]);
      if (!s) s = subjects.find((x) => norm(x.name).startsWith(t) || t.startsWith(norm(x.name)));
      if (s) {
        if (!ids.includes(s.id)) { ids.push(s.id); matched.push(s.name); }
      } else unmatched.push(raw.trim());
    });
    return { ids, matched, unmatched };
  };
  const targetOf = (combo) => (mode === "exam" ? matchExam(combo).ids : combo.subjectIds || []);

  // Tổng số tiết/tuần THEO QUY ĐỊNH (trang "Số tiết": CK+TC+CĐ, Học kỳ 1, theo
  // Loại lớp × Khối) — chỉ để tham khảo, KHÔNG cho sửa ở đây.
  const regulationPeriods = (cls, subjectId) => {
    const loaiKey = cls.classType === "LỚP CHỌN" ? "chon" : "thuong";
    const khoi = (String(cls.name).match(/^(\d{1,2})A/) || [])[1];
    const cell = (((rules.hk1 || {})[loaiKey] || {})[khoi] || {})[subjectId];
    if (!cell) return null;
    return (Number(cell.ck) || 0) + (Number(cell.tc) || 0) + (Number(cell.cd) || 0);
  };

  // Số tiết văn hoá khả dụng của lớp trong tuần (số buổi văn hoá × tiết/buổi),
  // trừ đi các tiết bị GHIM CỨNG ngoài môn học thường (không nằm trong quyền
  // sửa của "Cấu hình môn học") nhưng vẫn chiếm đúng 1 tiết văn hoá thật:
  //  - Sinh hoạt: GVCN đứng lớp (có dạy môn ở chính lớp mình chủ nhiệm) thì
  //    LUÔN ăn đúng 1 tiết văn hoá của lớp (xem timetable.js applySinhHoatByHomeroom).
  //  - Chào cờ: khối 10, Thứ 4 (index 2) buổi chiều — chỉ trừ nếu buổi đó
  //    ĐANG là văn hoá (nếu rơi vào buổi nghề/nghỉ thì không ăn tiết nào).
  const vanhoaTiet = (cls) => {
    const row = distByName[cls.name];
    if (!row) return null; // chưa tạo "Lịch phân phối" cho lớp này
    let tiet = 0, buoi = 0;
    (row.cells || []).forEach((c) => {
      if (c.sang === "vanhoa") { tiet += perBuoi; buoi += 1; }
      if (c.chieu === "vanhoa") { tiet += perBuoi; buoi += 1; }
    });
    const gvcnTeachesHere = cls.homeroomTeacherId && (cls.subjects || []).some((s) => s.assignedTeacherId === cls.homeroomTeacherId);
    if (gvcnTeachesHere) tiet -= 1;
    if (/^10A/.test(cls.name) && row.cells && row.cells[2] && row.cells[2].chieu === "vanhoa") tiet -= 1;
    return { tiet, buoi };
  };

  // Ưu tiên lấy ĐÚNG số tiết theo QUY ĐỊNH (trang "Số tiết") cho từng môn — nếu
  // tổng QĐ vừa/dư sức chứa thì lấy thẳng bằng QĐ (không cắt xén). Chỉ khi tổng
  // QĐ VƯỢT sức chứa mới phải lấp dần theo thứ tự ưu tiên (Văn/Toán trước) tới
  // khi hết chỗ, tránh xé lẻ 1 tiết ở bước cắt xén đó. Đây chỉ là GỢI Ý mặc định
  // — người dùng sửa tay được ở bảng (cũng bị chặn không vượt quy định).
  const defaultPeriodsFor = (cls) => {
    const combo = comboById[cls.combinationId];
    if (!combo) return null;
    const cap = vanhoaTiet(cls);
    if (!cap) return { noDist: true };
    const capacity = cap.tiet;
    const target = targetOf(combo);
    const subs = target.map((sid, i) => ({ sid, main: isMainNorm(norm(subjectName(sid))), i, reg: regulationPeriods(cls, sid) }));
    subs.sort((a, b) => (b.main ? wMain : wOther) - (a.main ? wMain : wOther) || a.i - b.i);
    const regSum = subs.reduce((s, x) => s + (x.reg || 0), 0);
    const periods = {};
    if (regSum <= capacity) {
      // Đủ/dư sức chứa: lấy ĐÚNG QĐ cho từng môn, KHÔNG tự ý gán thêm tiết dư
      // vào môn nào cả — dư ra thì để trống, đúng quy định là đủ.
      subs.forEach((x) => { if (x.reg) periods[x.sid] = x.reg; });
    } else {
      // Vượt sức chứa: bắt đầu từ đủ QĐ cho tất cả các môn, rồi cắt dần 1
      // tiết mỗi vòng từ môn ƯU TIÊN CAO nhất (Văn/Toán trước) cho tới khi
      // vừa sức chứa — để môn đứng cuối danh sách không bị dồn về 0 chỉ vì
      // thứ tự, mà san sẻ đều từ những môn có nhiều tiết hơn.
      subs.forEach((x) => { if (x.reg) periods[x.sid] = x.reg; });
      let over = regSum - capacity;
      let idx = 0;
      while (over > 0 && subs.some((x) => periods[x.sid] > 0)) {
        const x = subs[idx % subs.length];
        if (periods[x.sid] > 0) { periods[x.sid] -= 1; over -= 1; }
        idx += 1;
      }
    }
    return { capacity, buoi: cap.buoi, periods };
  };
  // Số tiết thực dùng = số người dùng sửa tay (nếu có) chồng lên gợi ý mặc định.
  // Luôn duyệt qua TẤT CẢ môn của tổ hợp (không chỉ tập môn theo "chế độ") để
  // môn nào người dùng lỡ điền tay dù không nằm trong gợi ý mặc định vẫn được lưu.
  const periodsFor = (cls) => {
    const base = defaultPeriodsFor(cls);
    if (!base || base.noDist) return base;
    const combo = comboById[cls.combinationId];
    const periods = {};
    (combo.subjectIds || []).forEach((sid) => {
      const key = `${cls.id}|${sid}`;
      const v = edited[key] !== undefined ? Number(edited[key]) || 0 : base.periods[sid] || 0;
      if (v > 0) periods[sid] = v;
    });
    return { capacity: base.capacity, buoi: base.buoi, periods };
  };
  // Tổng số tiết/tuần THEO QUY ĐỊNH cho CẢ tổ hợp (cộng dồn tất cả môn trong
  // tổ hợp, không chỉ môn thi tốt nghiệp) — để biết tổ hợp này phải học tổng
  // bao nhiêu tiết/tuần. missing = có môn nào chưa cấu hình QĐ (trang "Số tiết").
  const comboRegTotal = (cls, combo) => {
    let sum = 0, missing = [];
    (combo.subjectIds || []).forEach((sid) => {
      const reg = regulationPeriods(cls, sid);
      if (reg == null) missing.push(subjectName(sid));
      else sum += reg;
    });
    return { sum, missing };
  };

  const activeClasses = classes.filter((c) => c.status !== "giai_tan");
  const activeKhois = Array.isArray(khois) ? khois.filter((k) => ["12", "11", "10"].includes(String(k))) : [];
  const weekClasses =
    activeKhois.length > 0 && activeKhois.length < 3
      ? activeClasses.filter((c) => activeKhois.includes((String(c.name).match(/^(\d+)/) || [])[1]))
      : activeClasses;
  const targets = weekClasses.filter((c) => c.combinationId && comboById[c.combinationId]);
  const classesNoCombo = weekClasses.filter((c) => !c.combinationId);
  const noDistRows = targets.filter((c) => !distByName[c.name]);

  const apply = async () => {
    setError(""); setMsg("");
    if (targets.length === 0) {
      setError('Không lớp nào trong tuần đã gán tổ hợp. Vào trang "Lớp học" gán tổ hợp trước.');
      return;
    }
    if (noDistRows.length) {
      setError('Chưa có "Lịch phân phối nghề-văn hoá" cho các lớp này (cần số buổi văn hoá để chia tiết). Vào node "Lịch phân phối" bấm "Tạo lại từ TKB nghề" trước: ' + noDistRows.map((c) => c.name).join(", "));
      return;
    }
    if (mode === "exam") {
      const empty = targets.filter((c) => matchExam(comboById[c.combinationId]).ids.length === 0);
      if (empty.length) { setError("Không dò được môn thi tốt nghiệp cho: " + Array.from(new Set(empty.map((c) => comboById[c.combinationId].name))).join(", ")); return; }
    }
    let totalSub = 0;
    targets.forEach((c) => { totalSub += Object.keys(periodsFor(c).periods || {}).length; });
    const label = mode === "exam" ? "CHỈ môn thi tốt nghiệp" : "TẤT CẢ môn tổ hợp";
    if (!window.confirm(
      `Áp dụng "${label}" cho ${targets.length} lớp tuần này (tỉ lệ Văn/Toán=${wMain}, khác=${wOther}, chia theo số buổi văn hoá):\n` +
      `• Lưu danh sách môn + số tiết cho tuần này (tổng ${totalSub} môn).\n` +
      `• KHÔNG đụng "Phân công chuyên môn" — giáo viên đã gán vẫn giữ nguyên, lấy lúc xếp lịch.\n` +
      (classesNoCombo.length ? `• ${classesNoCombo.length} lớp chưa gán tổ hợp: bỏ qua.\n` : "") +
      `Tiếp tục?`
    )) return;

    setApplying(true);
    try {
      // Kho riêng theo tuần: { classId: { subjectId: số tiết } }. KHÔNG ghi vào classSubjects.
      const plan = {};
      for (const c of targets) {
        const periods = periodsFor(c).periods || {};
        const kept = {};
        Object.keys(periods).forEach((sid) => { if (periods[sid] > 0) kept[sid] = periods[sid]; });
        if (Object.keys(kept).length) plan[c.id] = kept;
      }
      await setWeekSubjectPlan(schoolId, weekId, plan);
      const now = Date.now();
      await setWeekConfig(schoolId, weekId, { subjectMode: mode, subjectModeAppliedAt: now, weightMain: wMain, weightOther: wOther, periodsPerVanhoaBuoi: perBuoi });
      setAppliedAt(now);
      setMsg(`Đã lưu cấu hình môn học tuần này cho ${targets.length} lớp. Nhớ gán giáo viên ở "Phân công chuyên môn".`);
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Chọn chế độ + tỉ lệ để hệ thống <strong>gợi ý</strong> số tiết mỗi môn theo cụm (Văn/Toán 3, môn khác 2, không xé lẻ 1
        tiết). Bạn <strong>sửa tay trực tiếp</strong> số tiết từng môn/lớp trong bảng (vd 12A1), rồi bấm <strong>Áp dụng</strong>.
        Bộ xếp TKB sẽ bám đúng số tiết này.
      </p>

      {/* Chế độ + tỉ lệ */}
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", margin: "10px 0 6px", alignItems: "center" }}>
        <label style={radioLbl}><input type="radio" checked={mode === "all"} onChange={() => setMode("all")} /> Học tất cả môn tổ hợp</label>
        <label style={radioLbl}><input type="radio" checked={mode === "exam"} onChange={() => setMode("exam")} /> Chỉ học môn thi tốt nghiệp</label>
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 8, fontSize: 13 }}>
        <span style={{ color: "#4b5563" }}>Tỉ lệ tiết:</span>
        <label>Văn/Toán <input type="number" min={1} value={wMain} onChange={(e) => setWMain(Math.max(1, Number(e.target.value) || 1))} style={numInput} /></label>
        <label>Môn khác <input type="number" min={1} value={wOther} onChange={(e) => setWOther(Math.max(1, Number(e.target.value) || 1))} style={numInput} /></label>
        <label style={{ borderLeft: "1px solid #e5e7eb", paddingLeft: 14 }}>Tiết/buổi văn hoá <input type="number" min={1} max={10} value={perBuoi} onChange={(e) => setPerBuoi(Math.max(1, Number(e.target.value) || 1))} style={numInput} /></label>
      </div>

      {appliedAt && (
        <p style={{ fontSize: 12, color: "#059669", marginTop: 0 }}>
          ✓ Đã áp dụng <strong>{mode === "exam" ? "Chỉ môn thi tốt nghiệp" : "Tất cả môn tổ hợp"}</strong> lúc {new Date(appliedAt).toLocaleString("vi-VN")}.
        </p>
      )}
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {msg && <p style={{ color: "#059669" }}>{msg}</p>}
      {noDistRows.length > 0 && (
        <p style={{ color: "#b45309", fontSize: 12 }}>
          ⚠ Chưa có Lịch phân phối cho: {noDistRows.map((c) => c.name).join(", ")} — vào <Link to="#">node "Lịch phân phối nghề-văn hoá"</Link> bấm "Tạo lại từ TKB nghề".
        </p>
      )}

      {/* Bảng xem trước */}
      {targets.length > 0 && (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 14 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
            <thead>
              <tr>
                <th style={th}>Lớp</th>
                <th style={th}>Tổ hợp</th>
                <th style={th}>Buổi VH</th>
                <th style={th}>Sức chứa</th>
                <th style={{ ...th, textAlign: "left" }}>Số tiết mỗi môn — đủ tất cả môn của tổ hợp (sửa được)</th>
                <th style={th}>Tổng nhập</th>
                <th style={th}>Tổng QĐ tổ hợp</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((c) => {
                const combo = comboById[c.combinationId];
                const base = defaultPeriodsFor(c);
                const tIds = combo.subjectIds || []; // luôn hiện ĐỦ môn của tổ hợp, không lọc theo chế độ
                const eff = (sid) => { const k = `${c.id}|${sid}`; return edited[k] !== undefined ? edited[k] : (base && base.periods[sid]) || 0; };
                const sum = tIds.reduce((s, sid) => s + (Number(eff(sid)) || 0), 0);
                const regTotal = comboRegTotal(c, combo);
                return (
                  <tr key={c.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{c.name}</td>
                    <td style={td}>{combo.name}</td>
                    <td style={{ ...td, textAlign: "center" }}>{base && !base.noDist ? base.buoi : "—"}</td>
                    <td style={{ ...td, textAlign: "center" }}>{base && !base.noDist ? base.capacity : "—"}</td>
                    <td style={{ ...td, textAlign: "left" }}>
                      {!base || base.noDist ? (
                        <span style={{ color: "#b45309" }}>chưa có lịch phân phối</span>
                      ) : (
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          {tIds.map((sid) => {
                            const reg = regulationPeriods(c, sid);
                            return (
                              <label key={sid} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                {subjectName(sid)}
                                <input type="number" min={0} max={reg == null ? undefined : reg} value={eff(sid)}
                                  onChange={(e) => setEdited((m) => {
                                    let v = e.target.value === "" ? 0 : Math.max(0, Number(e.target.value) || 0);
                                    if (reg != null && v > reg) v = reg; // không cho vượt quy định
                                    return { ...m, [`${c.id}|${sid}`]: v };
                                  })}
                                  style={{ width: 42, padding: 3, border: "1px solid #d1d5db", borderRadius: 5, textAlign: "center" }} />
                                <span title="Tổng tiết/tuần theo quy định (trang Số tiết) — chỉ tham khảo, không sửa ở đây" style={{ fontSize: 10, color: reg == null ? "#dc2626" : "#9ca3af" }}>
                                  (QĐ: {reg == null ? "chưa cấu hình" : reg})
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "center", color: base && !base.noDist && sum > base.capacity ? "#dc2626" : "#6b7280", fontWeight: 600 }}>
                      {base && !base.noDist ? `${sum}/${base.capacity}` : "—"}
                    </td>
                    <td style={{ ...td, textAlign: "center", fontWeight: 600 }}>
                      {regTotal.sum || "—"}
                      {regTotal.missing.length > 0 && (
                        <div title={`Chưa cấu hình QĐ (trang "Số tiết") cho: ${regTotal.missing.join(", ")}`} style={{ fontSize: 10, color: "#dc2626", fontWeight: 400 }}>
                          thiếu QĐ: {regTotal.missing.join(", ")}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <button type="button" onClick={apply} disabled={applying} style={applyBtn}>
        {applying ? "Đang áp dụng..." : `Áp dụng cho ${targets.length} lớp tuần này`}
      </button>
      {classesNoCombo.length > 0 && (
        <p style={{ color: "#b45309", fontSize: 12, marginTop: 8 }}>
          {classesNoCombo.length} lớp chưa gán tổ hợp (bỏ qua): {classesNoCombo.map((c) => c.name).join(", ")}
        </p>
      )}
      <p style={{ color: "#6b7280", fontSize: 12, marginTop: 10 }}>
        Sửa danh sách môn / môn thi tốt nghiệp ở trang <Link to="/happyschool/combinations">Tổ hợp bộ môn</Link>; gán tổ hợp cho lớp ở <Link to="/happyschool/classes">Lớp học</Link>.
      </p>
    </div>
  );
};

const radioLbl = { fontSize: 14, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" };
const numInput = { width: 52, padding: 4, marginLeft: 4, border: "1px solid #d1d5db", borderRadius: 6 };
const applyBtn = { padding: "10px 18px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const th = { border: "1px solid #e5e7eb", padding: "5px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "5px 8px" };

export default HappySchoolSubjectConfig;
